// TimelineWebApp/src/telemetry/telemetryStore.ts
import { create } from 'zustand';
import type { QueuedEvent } from './eventCatalog';

/** Mirrors Unity's tier ladder (TimelineWebViewPanel.GetTelemetryEntitlement) and the
 *  `entitlement` column on user_events. Keep in sync with events-ingest ALLOWED_ENTITLEMENTS. */
export const TELEMETRY_ENTITLEMENTS = ['trial', 'lifetime', 'yearly', 'monthly', 'free'] as const;
export type TelemetryEntitlement = (typeof TELEMETRY_ENTITLEMENTS)[number];
export function isTelemetryEntitlement(v: unknown): v is TelemetryEntitlement {
  return typeof v === 'string' && (TELEMETRY_ENTITLEMENTS as readonly string[]).includes(v);
}

interface TelemetryState {
  deviceId: string | null;
  sessionId: string;
  /** Funnel ID minted once per Unity app launch and broadcast to every
   *  WebView via the setFunnelId bridge message. Lets us stitch events
   *  emitted from different WebViews (Welcome → Timeline → ...) into one
   *  funnel attempt — sessionId resets per WebView mount and can't span
   *  the boundary on its own. Null in standalone web (where there is no
   *  Unity host to mint one); in that case events fall back to deviceId
   *  + sessionId correlation. */
  funnelId: string | null;
  /** App Store entitlement as Unity reports it: trial | lifetime | yearly | monthly | free.
   *  Arrives on the setDeviceId handshake and is refreshed by setSubscriptionState.entitlement
   *  (Timeline + Projects routes; the same guarded value, never that message's `tier`, which
   *  reads "free" before the receipt loads) so a trial start or purchase mid-session is captured.
   *  Null in standalone web and on hosts that predate the field. Stamped into every
   *  event's metadata (trackEvent) and lifted into user_events.entitlement by ingest. */
  entitlement: TelemetryEntitlement | null;
  queue: QueuedEvent[];
  /** Holds events fired before `deviceId` lands. Bounded at 10; excess dropped. */
  preDeviceIdBuffer: QueuedEvent[];
  flushTimer: number | null;
  /** Project source set from the setSqlitePath bridge message. Used to tag
   *  any later timeline-origin events as needed. Null before a project is loaded. */
  projectSource: 'sample' | 'user_project' | 'imported' | null;
  /** Whether the current project was auto-opened by Unity (welcome-flow
   *  "View Example" path). Set from the setSqlitePath bridge payload.
   *  Null before a project is loaded; false on a manually-opened project. */
  autoOpened: boolean | null;

  setDeviceId: (id: string) => void;
  setFunnelId: (id: string) => void;
  setEntitlement: (value: TelemetryEntitlement | null) => void;
  setProjectSource: (source: 'sample' | 'user_project' | 'imported') => void;
  setAutoOpened: (value: boolean) => void;
}

// Session ID generated once per module load (i.e. per web-view mount).
// crypto.randomUUID is available in modern WebKit; fall back for safety.
function generateSessionId(): string {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  const hex = '0123456789abcdef';
  let uuid = '';
  for (let i = 0; i < 36; i++) {
    if (i === 8 || i === 13 || i === 18 || i === 23) uuid += '-';
    else if (i === 14) uuid += '4';
    else if (i === 19) uuid += hex[8 + Math.floor(Math.random() * 4)];
    else uuid += hex[Math.floor(Math.random() * 16)];
  }
  return uuid;
}

export const useTelemetryStore = create<TelemetryState>((set, get) => ({
  deviceId: null,
  sessionId: generateSessionId(),
  funnelId: null,
  entitlement: null,
  queue: [],
  preDeviceIdBuffer: [],
  flushTimer: null,
  projectSource: null,
  autoOpened: null,

  setDeviceId: (id: string) => {
    const { preDeviceIdBuffer, queue } = get();
    // Migrate buffered events into the main queue on device-id arrival.
    set({
      deviceId: id,
      queue: [...queue, ...preDeviceIdBuffer],
      preDeviceIdBuffer: [],
    });
  },

  setFunnelId: (id: string) => set({ funnelId: id }),

  setEntitlement: (value) => set({ entitlement: value }),

  setProjectSource: (source) => set({ projectSource: source }),

  setAutoOpened: (value) => set({ autoOpened: value }),
}));
